import { Button } from "@exopengithub/exokit/v2";
import { IconCheck, IconPlayerPlay } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { Card } from "src/features/planning/components/Card.js";
import { experienceCatalogue } from "./catalogue.js";
import { useExperience } from "./useExperience.js";
import { glanceDateFormatOptions } from "src/common/utils/date-utils/glanceDateFormat.js";
import { useLocale } from "src/locales/useLocale.js";

const ExperienceRow = ({
	experienceId,
	onShowAgain,
}: {
	experienceId: string;
	onShowAgain: () => void;
}) => {
	const { t } = useTranslation();
	const { format } = useLocale();
	const { experience, completed, completedAt, reset } =
		useExperience(experienceId);

	if (!experience) {
		return null;
	}

	return (
		<div className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0">
			<div className="min-w-0 space-y-0.5">
				<p className="text-gray-900 text-sm">{t(experience.name)}</p>
				<p className="flex items-center gap-1.5 text-gray-500 text-xs">
					<span>{t("{{count}} step", { count: experience.steps.length })}</span>
					<span aria-hidden>·</span>
					{completed && completedAt ? (
						<span className="inline-flex items-center gap-1 text-v2-green-700">
							<IconCheck aria-hidden size={12} stroke={2} />
							{t("Completed {{when}}", {
								when: format(
									new Date(completedAt),
									glanceDateFormatOptions(new Date(completedAt)),
								),
							})}
						</span>
					) : (
						<span>{t("Not seen yet")}</span>
					)}
				</p>
			</div>
			{completed && (
				<Button
					icon={<IconPlayerPlay />}
					onClick={() => {
						reset();
						onShowAgain();
					}}
					variant="ghost"
				>
					{t("Show again")}
				</Button>
			)}
		</div>
	);
};

export const ExperienceList = ({
	onShowAgain,
}: {
	onShowAgain: () => void;
}) => {
	const { t } = useTranslation();
	const ids = Object.keys(experienceCatalogue);

	if (ids.length === 0) {
		return null;
	}

	return (
		<Card>
			<div className="p-6">
				<div className="mb-3 font-medium text-gray-700 text-sm">
					{t("Guides")}
				</div>
				<div className="divide-y divide-gray-100">
					{ids.map((experienceId) => (
						<ExperienceRow
							experienceId={experienceId}
							key={experienceId}
							onShowAgain={onShowAgain}
						/>
					))}
				</div>
			</div>
		</Card>
	);
};
